import { t } from "@redotech/redo-i18n";
import { TrackerStatus } from "@redotech/redo-model/order-tracking/order-tracking-tracker-status-definition";

const exceptionalStatuses = [
  TrackerStatus.FAILURE,
  TrackerStatus.CANCELLED,
  TrackerStatus.ERROR,
  TrackerStatus.RETURN_TO_SENDER,
] as const satisfies readonly TrackerStatus[];

export type ExceptionalStatus = (typeof exceptionalStatuses)[number];

export const exceptionalStatusDisplayStrings: Record<
  ExceptionalStatus,
  string
> = {
  [TrackerStatus.FAILURE]: t("Failure"),
  [TrackerStatus.CANCELLED]: t("Cancelled"),
  [TrackerStatus.ERROR]: t("Error"),
  [TrackerStatus.RETURN_TO_SENDER]: t("Return to sender"),
};

export const exceptionalStatusVerbiage: Record<
  ExceptionalStatus,
  (merchantName: string) => string
> = {
  [TrackerStatus.FAILURE]: (_) =>
    t("Delivery has failed. Please contact carrier for details"),
  [TrackerStatus.CANCELLED]: (merchantName) =>
    t(
      "This shipment has been cancelled. Please contact {{merchantName}} for more information.",
      { merchantName },
    ),
  [TrackerStatus.ERROR]: (_) =>
    t("Unknown delivery error, please contact the carrier for more details."),
  [TrackerStatus.RETURN_TO_SENDER]: (merchantName) =>
    t(
      "Carrier was not able to deliver package. Please contact {{merchantName}} for more information.",
      { merchantName },
    ),
};

// when a failure status is reached, it stays as that status until
// one of the following statuses follows it, after which it is unfrozen
const recoveryStatuses: Record<ExceptionalStatus, TrackerStatus[]> = {
  [TrackerStatus.FAILURE]: [TrackerStatus.DELIVERED],
  [TrackerStatus.CANCELLED]: [],
  [TrackerStatus.ERROR]: [TrackerStatus.DELIVERED],
  [TrackerStatus.RETURN_TO_SENDER]: [],
};

const exceptionalStatusSet: ReadonlySet<TrackerStatus> = new Set(
  exceptionalStatuses,
);

export function getMostRecentUnrecoveredExceptionalStatus(
  trackingEvents: { status: TrackerStatus }[],
) {
  return trackingEvents.find(
    (detail, index) =>
      exceptionalStatusSet.has(detail.status) &&
      !trackingEvents
        .slice(index + 1)
        .some((d) =>
          recoveryStatuses[detail.status as ExceptionalStatus].includes(
            d.status,
          ),
        ),
  );
}
